/** Raqam, sana va vaqtni joriy tilga moslab formatlash. */

export function formatNumber(value: number, locale: string, digits = 0): string {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

/**
 * O'zbekcha qisqartmalar. Brauzer ICU ma'lumotlarida `uz` uchun ixcham yozuv
 * yo'q — `Intl` «7G» (giga) qaytaradi, shuning uchun o'zbek tilida qadamlar
 * qo'lda beriladi.
 */
const UZ_COMPACT: Array<[number, string]> = [
  [1e12, "trln"],
  [1e9, "mlrd"],
  [1e6, "mln"],
  [1e3, "ming"],
];

/** Katta sonlarni qisqartirish: 12 480 → «12,5 ming». */
export function formatCompact(value: number, locale: string): string {
  if (locale.startsWith("uz")) {
    for (const [step, suffix] of UZ_COMPACT) {
      if (Math.abs(value) >= step) return `${formatNumber(value / step, locale, 1)} ${suffix}`;
    }
    return formatNumber(value, locale);
  }
  return new Intl.NumberFormat(locale, {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * O'zbekcha oy nomlari.
 *
 * Brauzer ICU ma'lumotlarida `uz` uchun oy nomlari yo'q — `Intl` «2026 M08 14»
 * qaytaradi. Shuning uchun o'zbek tilida sana qo'lda yig'iladi, qolgan tillar
 * uchun `Intl` ishlatiladi.
 */
const UZ_MONTHS = [
  "yanvar",
  "fevral",
  "mart",
  "aprel",
  "may",
  "iyun",
  "iyul",
  "avgust",
  "sentabr",
  "oktabr",
  "noyabr",
  "dekabr",
];

export function formatDate(value: Date | string, locale: string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  if (locale.startsWith("uz")) {
    return `${date.getDate()}-${UZ_MONTHS[date.getMonth()]}, ${date.getFullYear()}`;
  }
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function formatDateShort(value: Date | string, locale: string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit" }).format(date);
}

export function formatTime(value: Date | string, locale: string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  return new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(date);
}

/** «12 daqiqa oldin» ko'rinishidagi nisbiy vaqt. */
export function formatRelative(minutesAgo: number, locale: string): string {
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  if (minutesAgo < 60) return rtf.format(-Math.round(minutesAgo), "minute");
  if (minutesAgo < 60 * 24) return rtf.format(-Math.round(minutesAgo / 60), "hour");
  return rtf.format(-Math.round(minutesAgo / (60 * 24)), "day");
}

export function formatPercent(value: number, locale: string, digits = 1): string {
  return `${formatNumber(value, locale, digits)}%`;
}

/** Ikki qiymat orasidagi o'zgarishni foizda hisoblash. */
export function delta(current: number, previous: number): number {
  if (!previous) return 0;
  return ((current - previous) / previous) * 100;
}

/**
 * Pul summasi. Backend `Decimal` ni satr sifatida qaytaradi, shuning uchun
 * kirish qiymati ham satr, ham son bo'lishi mumkin. Katta summalar
 * qisqartiriladi (masalan «418,1 mln so'm») — jadval ustuni tor bo'ladi.
 */
export function formatMoney(value: string | number | null | undefined, locale: string): string {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) return "—";
  if (Math.abs(amount) >= 1_000_000) {
    return `${formatCompact(amount, locale)} so'm`;
  }
  return `${formatNumber(amount, locale)} so'm`;
}

/** O'nlik qiymat (maydon, miqdor): satr yoki son. */
export function formatDecimal(
  value: string | number | null | undefined,
  locale: string,
  digits = 2,
): string {
  const amount = Number(value ?? 0);
  if (!Number.isFinite(amount)) return "—";
  return formatNumber(amount, locale, digits);
}

/** Sana va vaqt — jadvallardagi «qachon» ustuni uchun. */
export function formatDateTime(value: string | Date | null | undefined, locale: string): string {
  if (!value) return "—";
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "—";
  return `${formatDateShort(date, locale)} ${new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date)}`;
}

/** Sana (vaqtsiz); bo'sh qiymat uchun chiziqcha. */
export function formatDay(value: string | Date | null | undefined, locale: string): string {
  if (!value) return "—";
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return "—";
  return formatDate(date, locale);
}
